/**
 * CLI 28 — Pestaña "Mi uso" del operador logueado.
 *
 * Estado de filtros en URL query string (shareable). El backend filtra
 * por user_id automáticamente; este componente NUNCA mete user_id en
 * la query (el usuario común no puede ver datos ajenos).
 *
 * AP-11: el gráfico y las tablas traducen `tier` interno a etiquetas
 * públicas (`Rápido`/`Equilibrado`/`Profundo`). Provider y model
 * privado NUNCA aparecen.
 */
import { useMemo } from "react";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useSearchParams } from "react-router-dom";

import { useSelfUsage, type UsageFilters } from "@/hooks/useUsage";
import {
  featureLabel,
  formatNumber,
  formatMxn,
  formatUsd,
  rangePreset,
  tierLabel,
} from "@/lib/usageHelpers";


function parseFiltersFromUrl(params: URLSearchParams): UsageFilters {
  const granularityRaw = params.get("granularity") ?? "day";
  const granularity: "day" | "hour" = granularityRaw === "hour" ? "hour" : "day";
  return {
    since: params.get("since") ?? undefined,
    until: params.get("until") ?? undefined,
    tier: params.get("tier") ?? undefined,
    feature: params.get("feature") ?? undefined,
    granularity,
  };
}


export function UsagePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFiltersFromUrl(searchParams), [searchParams]);

  const setRangePreset = (preset: "last_7_days" | "last_30_days") => {
    const range = rangePreset(preset);
    const next = new URLSearchParams(searchParams);
    if (range.since) next.set("since", range.since);
    if (range.until) next.set("until", range.until);
    setSearchParams(next, { replace: true });
  };

  const setQueryParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const { data, isLoading, error } = useSelfUsage(filters);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Mi uso</h1>
        <p className="text-sm text-muted-foreground">
          Consumo de capacidad analítica de AROC en el rango seleccionado.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <button
          className="px-3 py-1 text-sm rounded-md border bg-card hover:bg-accent"
          onClick={() => setRangePreset("last_7_days")}
        >
          Últimos 7 días
        </button>
        <button
          className="px-3 py-1 text-sm rounded-md border bg-card hover:bg-accent"
          onClick={() => setRangePreset("last_30_days")}
        >
          Últimos 30 días
        </button>
        <label className="text-sm flex items-center gap-2">
          Desde:
          <input
            type="date"
            value={filters.since ?? ""}
            onChange={(e) => setQueryParam("since", e.target.value)}
            className="px-2 py-1 text-sm rounded-md border"
          />
        </label>
        <label className="text-sm flex items-center gap-2">
          Hasta:
          <input
            type="date"
            value={filters.until ?? ""}
            onChange={(e) => setQueryParam("until", e.target.value)}
            className="px-2 py-1 text-sm rounded-md border"
          />
        </label>
        <label className="text-sm flex items-center gap-2">
          Granularidad:
          <select
            value={filters.granularity ?? "day"}
            onChange={(e) => setQueryParam("granularity", e.target.value)}
            className="px-2 py-1 text-sm rounded-md border"
          >
            <option value="day">Día</option>
            <option value="hour">Hora</option>
          </select>
        </label>
      </div>

      {isLoading && <div className="text-sm text-muted-foreground">Cargando…</div>}
      {error && (
        <div className="text-sm text-destructive">
          No se pudo obtener el uso: {(error as Error).message}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard label="Llamadas" value={formatNumber(data.totals.calls)} />
            <StatCard
              label="Tokens totales"
              value={formatNumber(
                data.totals.input_tokens +
                  data.totals.output_tokens +
                  data.totals.cache_read_tokens +
                  data.totals.cache_write_tokens,
              )}
            />
            <StatCard label="Errores" value={formatNumber(data.totals.errors)} />
            <StatCard
              label="Costo estimado"
              value={
                data.totals.estimated_cost_mxn === null
                  ? formatUsd(data.totals.estimated_cost_usd)
                  : formatMxn(data.totals.estimated_cost_mxn)
              }
              sub={
                data.totals.estimated_cost_mxn === null
                  ? "Falta el tipo de cambio: moneda original"
                  : formatUsd(data.totals.estimated_cost_usd)
              }
            />
          </div>

          <div className="rounded-lg border bg-card p-4">
            <h2 className="text-sm font-medium mb-3">Tokens por periodo</h2>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.by_period.map((b) => ({
                    period: b.period.slice(0, filters.granularity === "hour" ? 13 : 10),
                    input: b.input_tokens,
                    output: b.output_tokens,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="input" name="Entrada" stackId="a" fill="#3b82f6" />
                  <Bar dataKey="output" name="Salida" stackId="a" fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TableCard title="Por capacidad analítica">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1">Capacidad</th>
                    <th className="py-1 text-right">Llamadas</th>
                    <th className="py-1 text-right">Tokens</th>
                    <th className="py-1 text-right">Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_feature.map((r) => (
                    <tr key={r.feature} className="border-t">
                      <td className="py-1">{featureLabel(r.feature)}</td>
                      <td className="py-1 text-right">{formatNumber(r.calls)}</td>
                      <td className="py-1 text-right">{formatNumber(r.tokens)}</td>
                      <td className="py-1 text-right">{formatUsd(r.estimated_cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableCard>
            <TableCard title="Por modo de respuesta">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1">Modo</th>
                    <th className="py-1 text-right">Llamadas</th>
                    <th className="py-1 text-right">Tokens</th>
                    <th className="py-1 text-right">Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_tier.map((r) => (
                    <tr key={r.tier} className="border-t">
                      <td className="py-1">{tierLabel(r.tier)}</td>
                      <td className="py-1 text-right">{formatNumber(r.calls)}</td>
                      <td className="py-1 text-right">{formatNumber(r.tokens)}</td>
                      <td className="py-1 text-right">{formatUsd(r.estimated_cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableCard>
          </div>
        </>
      )}
    </div>
  );
}


function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  /** Linea chica bajo el numero: la moneda de la factura, o por que falta. */
  sub?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}


function TableCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <h2 className="text-sm font-medium mb-3">{title}</h2>
      {children}
    </div>
  );
}
